import axios from "axios";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? "/api" : "http://localhost:5000/api");

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies for CSRF
});

// CSRF token management
let csrfToken = null;

const getCsrfToken = async () => {
  if (!csrfToken) {
    try {
      const response = await axios.get(`${API_BASE_URL}/csrf-token`, {
        withCredentials: true,
      });
      csrfToken = response.data.csrfToken;
    } catch (error) {
      console.warn("Could not fetch CSRF token:", error.message);
    }
  }
  return csrfToken;
};

// Add Authorization and CSRF headers
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing requests
    if (
      ["post", "put", "patch", "delete"].includes(config.method?.toLowerCase())
    ) {
      const csrf = await getCsrfToken();
      if (csrf) {
        config.headers["X-CSRF-Token"] = csrf;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401/403/423 responses (expired/invalid token or blocked account)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorName = error.response?.data?.error;
    const errorDetails = error.response?.data?.details || [];

    // Network error or server unavailable (502/503)
    if (!error.response || status === 502 || status === 503) {
      window.dispatchEvent(new CustomEvent("api-unavailable"));
    }

    const shouldLogoutOnForbidden =
      status === 403 &&
      (errorName === "Authentication failed" ||
        errorName === "Authentication required" ||
        errorDetails.includes("No token provided") ||
        errorDetails.includes("Invalid token") ||
        errorDetails.includes("Token expired"));
    if (
      (status === 401 || status === 423 || shouldLogoutOnForbidden) &&
      window.location.pathname !== "/login" &&
      window.location.pathname !== "/signup" &&
      window.location.pathname !== "/"
    ) {
      const token = localStorage.getItem("token");
      if (token) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const articlesAPI = {
  getAllArticles: () => api.get("/articles"),
  getMyArticles: (contentType, metaOnly = false) =>
    api.get("/articles/my-articles", { params: { contentType, metaOnly } }),
  getLessonsBrowser: (folder = "") =>
    api.get("/articles/lessons-browser", { params: { folder } }),
  getArticleById: (id) => api.get(`/articles/${id}`),
  getFolders: (contentType, excludePlaceholders = false) =>
    api.get("/articles/folders", {
      params: { contentType, excludePlaceholders },
    }),
  getArticlesByFolder: (folderName, contentType) =>
    api.get(`/articles/folder/${encodeURIComponent(folderName)}`, { params: { contentType } }),
  getLessonItems: (lessonId, contentType, metaOnly = false) =>
    api.get(`/articles/lesson-items/${lessonId}`, { params: { contentType, metaOnly } }),
  getLessonLinksSummary: (contentType) =>
    api.get("/articles/lesson-links-summary", { params: { contentType } }),
  createArticle: ({ title, author, chapter, body, contentType, creatorId, folder, insertAfterIndex, parentLesson, lessonId, order, tags }) =>
    api.post("/articles", { title, author, chapter, body, contentType, creatorId, folder, insertAfterIndex, parentLesson, lessonId, order, tags }),
  bulkCreateNotes: ({ notes, lessonId, folderName, chapter, author }) =>
    api.post("/articles/bulk-notes", { notes, lessonId, folderName, chapter, author }),
  bulkDeleteNotes: (lessonId) =>
    api.delete(`/articles/bulk/${lessonId}/note`),
  generateNotesDraft: (lessonId, profile = "main_ideas_preserve_text") =>
    api.post(`/articles/${lessonId}/generate-notes-draft`, { profile }),
  bulkCreateFlashcards: ({ flashcards, lessonId, folderName, chapter, author }) =>
    api.post("/articles/bulk-flashcards", { flashcards, lessonId, folderName, chapter, author }),
  bulkDeleteFlashcards: (lessonId) =>
    api.delete(`/articles/bulk/${lessonId}/flashcard`),
  // DO NOT DELETE - bulkUpload temporarily disabled but kept for future use
  // bulkUpload: (fileContent, fileType, contentType) =>
  //   api.post("/articles/bulk-upload", { fileContent, fileType, contentType }),
  deleteArticle: (id) => api.delete(`/articles/${id}`),
  updateArticle: (id, data) =>
    api.put(`/articles/${id}`, data),
  getPublicArticles: () => api.get("/articles/public-articles"),
  getPublicArticleById: (lessonId) => api.get(`/articles/public-articles/${lessonId}`),
  getPublicNotes: (lessonId) => api.get(`/articles/public-notes/${lessonId}`),
  getPublicFlashcards: (lessonId) => api.get(`/articles/public-flashcards/${lessonId}`),
  toggleArticleStatus: (articleId) => api.put(`/articles/${articleId}/status`),
  toggleArticlePublic: (articleId, data) => api.put(`/articles/${articleId}/public`, data),
  generateShareLink: (articleId) => api.post(`/articles/${articleId}/generate-share-link`),
  getSharedArticle: (token) => api.get(`/articles/s/${token}`),
  reorderArticles: (folderName, orderedIds) => api.put(`/articles/reorder`, { folderName, orderedIds }),
  saveLesson: (lessonId, folderName = "Saved") => api.post(`/articles/${lessonId}/save`, { folderName }),
};

export const communitiesAPI = {
  createCommunity: ({ name, description = "" }) =>
    api.post("/communities", { name, description }),
  updateCommunity: (communityId, { name, description = "" }) =>
    api.put(`/communities/${communityId}`, { name, description }),
  getMine: (lessonId = "") =>
    api.get("/communities/mine", {
      params: lessonId ? { lessonId } : {},
    }),
  getJoined: () => api.get("/communities/joined"),
  getInvites: () => api.get("/communities/invites"),
  getRequests: () => api.get("/communities/requests"),
  getDiscover: () => api.get("/communities/discover"),
  getInboxCount: () => api.get("/communities/inbox/count"),
  getInbox: () => api.get("/communities/inbox"),
  deleteReadInbox: () => api.delete("/communities/inbox/read"),
  getNotifications: () => api.get("/communities/notifications"),
  markNotificationsRead: (notificationIds) =>
    api.post("/communities/notifications/read", { notificationIds }),
  deleteNotification: (notificationId) =>
    api.delete(`/communities/notifications/${notificationId}`),
  getPublicProfile: (communityId) =>
    api.get(`/communities/${communityId}/public-profile`),
  getCommunity: (communityId) => api.get(`/communities/${communityId}`),
  updateCommunitySettings: (communityId, { joinMode, isListed }) =>
    api.patch(`/communities/${communityId}/settings`, { joinMode, isListed }),
  getCommunityBoard: (communityId) =>
    api.get(`/communities/${communityId}/board`),
  updateCommunityBoard: (communityId, { boardTitle, boardBody }) =>
    api.put(`/communities/${communityId}/board`, { boardTitle, boardBody }),
  getCommunityMembers: (communityId) =>
    api.get(`/communities/${communityId}/members`),
  joinByCode: (joinCode) => api.post("/communities/join-by-code", { joinCode }),
  joinListedCommunity: (communityId) => api.post(`/communities/${communityId}/join`),
  cancelJoinRequest: (membershipId) =>
    api.delete(`/communities/requests/${membershipId}`),
  inviteByShareCode: (communityId, shareCode) =>
    api.post(`/communities/${communityId}/invites`, { shareCode }),
  cancelInvite: (communityId, membershipId) =>
    api.delete(`/communities/${communityId}/invites/${membershipId}`),
  approveJoinRequest: (communityId, membershipId) =>
    api.post(`/communities/${communityId}/requests/${membershipId}/approve`),
  rejectJoinRequest: (communityId, membershipId) =>
    api.post(`/communities/${communityId}/requests/${membershipId}/reject`),
  hideJoinRequest: (communityId, membershipId) =>
    api.post(`/communities/${communityId}/requests/${membershipId}/hide`),
  acceptInvite: (membershipId) =>
    api.post(`/communities/memberships/${membershipId}/accept`),
  markInviteRead: (membershipId) =>
    api.post(`/communities/memberships/${membershipId}/read`),
  rejectInvite: (membershipId) =>
    api.post(`/communities/memberships/${membershipId}/reject`),
  deleteInvite: (membershipId) =>
    api.delete(`/communities/memberships/${membershipId}/invite`),
  quitCommunity: (communityId) => api.post(`/communities/${communityId}/quit`),
  kickMember: (communityId, membershipId) =>
    api.post(`/communities/${communityId}/members/${membershipId}/kick`),
  archiveCommunity: (communityId) =>
    api.put(`/communities/${communityId}/archive`),
  restoreCommunity: (communityId) =>
    api.put(`/communities/${communityId}/restore`),
  deleteCommunity: (communityId) => api.delete(`/communities/${communityId}`),
  getCommunityLessons: (communityId) =>
    api.get(`/communities/${communityId}/lessons`),
  shareLesson: (communityId, lessonId) =>
    api.post(`/communities/${communityId}/lessons/${lessonId}/share`),
  unshareLesson: (communityId, lessonId) =>
    api.delete(`/communities/${communityId}/lessons/${lessonId}/share`),
  getFeedLessons: () => api.get("/communities/feed/lessons"),
  getLesson: (communityId, lessonId) =>
    api.get(`/communities/${communityId}/lessons/${lessonId}`),
  getLessonItems: (communityId, lessonId, contentType) =>
    api.get(`/communities/${communityId}/lessons/${lessonId}/items`, {
      params: { contentType },
    }),
  saveLesson: (communityId, lessonId, folderName = "Saved") =>
    api.post(`/communities/${communityId}/lessons/${lessonId}/save`, {
      folderName,
    }),
};

export const connectionsAPI = {
  getConnections: (view = "") =>
    api.get(view ? `/connections?view=${view}` : "/connections"),
  getInboxConnections: () => api.get("/connections/inbox"),
  startConnection: ({ targetUserId, body, contextType = "profile", communityId = "" }) =>
    api.post("/connections/start", {
      targetUserId,
      body,
      contextType,
      communityId,
    }),
  getTargetStatus: (targetUserId) =>
    api.get(`/connections/target/${targetUserId}/status`),
  getConnection: (connectionId) => api.get(`/connections/${connectionId}`),
  markConnectionRead: (connectionId) =>
    api.post(`/connections/${connectionId}/read`),
  markConnectionNotificationsRead: (ids) =>
    api.post("/connections/inbox/notifications/read", { ids }),
  deleteConnectionNotification: (notificationId) =>
    api.delete(`/connections/inbox/notifications/${notificationId}`),
  deleteReadConnectionNotifications: () =>
    api.delete("/connections/inbox/notifications/read"),
  hideConnection: (connectionId) => api.post(`/connections/${connectionId}/hide`),
  unhideConnection: (connectionId) =>
    api.post(`/connections/${connectionId}/unhide`),
  disconnectConnection: (connectionId) =>
    api.post(`/connections/${connectionId}/disconnect`),
  sendConnectionNote: (connectionId, body) =>
    api.post(`/connections/${connectionId}/notes`, { body }),
  sendConnectRequest: (targetUserId) =>
    api.post("/connections/request", { targetUserId }),
  getIncomingRequests: () => api.get("/connections/requests/incoming"),
  getSentRequests: () => api.get("/connections/requests/sent"),
  getAcceptedConnections: () => api.get("/connections/accepted"),
  acceptConnection: (connectionId) =>
    api.post(`/connections/${connectionId}/accept`),
  declineConnection: (connectionId) =>
    api.post(`/connections/${connectionId}/decline`),
};

export const statsAPI = {
  getOverview: () => api.get("/stats/overview"),
};

export const tagsAPI = {
  getApprovedTags: (appliesTo = "") =>
    api.get("/tags/approved", { params: appliesTo ? { appliesTo } : {} }),
};

export const forgotPassword = (email) => {
  return api.post("/auth/forgot-password", { email });
};

export const resetPassword = (token, password, passwordConfirm) => {
  return api.post("/auth/reset-password", { token, password, passwordConfirm });
};

export const profileAPI = {
  getProfile: () => api.get("/auth/profile"),
  getPublicProfile: () => api.get("/auth/public-profile"),
  updatePublicProfile: ({
    publicDisplayName,
    publicBio,
    publicInterests,
    publicProfileVisible,
  }) =>
    api.put("/auth/public-profile", {
      publicDisplayName,
      publicBio,
      publicInterests,
      publicProfileVisible,
    }),
  updateProfile: (displayName, theme, showDisplayName) => {
    const data = {};
    if (displayName !== undefined) data.displayName = displayName;
    if (theme !== undefined) data.theme = theme;
    if (showDisplayName !== undefined) data.showDisplayName = showDisplayName;
    return api.put("/auth/profile", data);
  },
  changePassword: (currentPassword, newPassword, confirmPassword) =>
    api.put("/auth/change-password", {
      currentPassword,
      newPassword,
      confirmPassword,
    }),
  deleteAccount: (password, confirmation) =>
    api.delete("/auth/delete-account", { data: { password, confirmation } }),
  changeEmailRequest: (newEmail, currentPassword) =>
    api.put("/auth/change-email-request", { newEmail, currentPassword }),
  verifyEmailChange: (token) =>
    api.get(`/auth/verify-email-change?token=${token}`),
  deactivateAccount: (password) =>
    api.put("/auth/deactivate-account", { password }),
  reactivateAccount: () => api.put("/auth/reactivate-account"),
  exportData: () => api.get("/auth/export-data", { responseType: "blob" }),
  upgradeTier: () => api.put("/auth/upgrade-tier"),
  cancelTier: () => api.put("/auth/cancel-tier"),
  dismissPlanIntro: () => api.put("/auth/dismiss-plan-intro"),
};

export const userProfilesAPI = {
  getPublicProfile: (userId) => api.get(`/users/${userId}/public-profile`),
  getDiscoverPeople: () => api.get("/users/discover"),
};

export default api;
export const contactAPI = {
  sendMessage: (name, email, subject, message) =>
    api.post("/contact/send", { name, email, subject, message }),
};
